import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ChevronRight, ShieldAlert, Loader2, CheckCircle, XCircle } from 'lucide-react';
import { createComponentLogger } from '@/utils/logger';
import { useAuth } from '@/contexts/useAuth';
import {
  notificationSettingsService,
  type AdditionalRecipient,
  type EncompassRoleOption,
  type NotificationPreview,
  type NotificationSetting,
  type SendCountSummary
} from '@/services/notificationSettingsService';
import { DetailPane } from './DetailPane';
import { MasterList } from './MasterList';
import {
  canEditRecipients,
  canEditTemplate,
  effectiveRecipientConfigFor
} from './config';
import type { RecipientConfigV1, TemplateOverrides } from './types';
import { NotificationDraftSaveError, saveNotificationDraft } from './saveNotificationDraft';
import './notification-management.css';

const log = createComponentLogger('NotificationManagement', 'notification-settings');

interface TestSendResult {
  type: string;
  success: boolean;
  message: string;
}

function overridesFromSetting(setting: NotificationSetting | null): TemplateOverrides {
  return {
    subject: setting?.template_subject_override ?? null,
    body: setting?.template_body_override ?? null
  };
}

function configFromSetting(setting: NotificationSetting | null): RecipientConfigV1 | null {
  if (!setting || !canEditRecipients(setting.notification_type)) {
    return null;
  }
  return effectiveRecipientConfigFor(setting);
}

export function NotificationManagement() {
  const { user } = useAuth();
  const [settings, setSettings] = useState<NotificationSetting[]>([]);
  const [availableUsers, setAvailableUsers] = useState<AdditionalRecipient[]>([]);
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [encompassRoles, setEncompassRoles] = useState<EncompassRoleOption[]>([]);
  const [encompassRolesError, setEncompassRolesError] = useState<string | null>(null);
  const [counts, setCounts] = useState<Record<string, SendCountSummary>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [draftConfig, setDraftConfig] = useState<RecipientConfigV1 | null>(null);
  const [draftOverrides, setDraftOverrides] = useState<TemplateOverrides>({});
  const [saving, setSaving] = useState(false);
  const [togglingType, setTogglingType] = useState<string | null>(null);
  const [sendingTestType, setSendingTestType] = useState<string | null>(null);
  const [testSendResult, setTestSendResult] = useState<TestSendResult | null>(null);
  const [preview, setPreview] = useState<NotificationPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [countsError, setCountsError] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(true);

  const selectedSetting = useMemo(
    () => settings.find((setting) => setting.notification_type === selectedType) ?? null,
    [settings, selectedType]
  );

  const persistedConfig = useMemo(() => configFromSetting(selectedSetting), [selectedSetting]);
  const persistedOverrides = useMemo(() => overridesFromSetting(selectedSetting), [selectedSetting]);
  const configDirty = selectedSetting
    ? JSON.stringify(draftConfig) !== JSON.stringify(persistedConfig)
    : false;
  const overridesDirty = selectedSetting
    ? JSON.stringify(draftOverrides) !== JSON.stringify(persistedOverrides)
    : false;
  const dirty = configDirty || overridesDirty;

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setCountsError(null);
      setEncompassRolesError(null);
      const [settingsData, usersData, rolesData, encompassRolesData, countsData] = await Promise.all([
        notificationSettingsService.getSettings(),
        notificationSettingsService.getAvailableUsers(),
        notificationSettingsService.getDistinctUserRoles(),
        notificationSettingsService.getEncompassRoles().catch((err) => {
          const message = err instanceof Error ? err.message : 'Unable to load Encompass roles';
          setEncompassRolesError(message);
          log.warn('Unable to load Encompass role catalog', {
            metadata: { error: message }
          });
          return [];
        }),
        notificationSettingsService.getSendCountSummaries().catch((err) => {
          const message = err instanceof Error ? err.message : 'Unable to load notification send counts';
          setCountsError(message);
          log.warn('Unable to load notification send counts', {
            metadata: { error: message }
          });
          return {};
        })
      ]);
      setSettings(settingsData);
      setAvailableUsers(usersData);
      setUserRoles(rolesData);
      setEncompassRoles(encompassRolesData);
      setCounts(countsData);
      setSelectedType((current) => current ?? settingsData[0]?.notification_type ?? null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load notification settings';
      setError(message);
      log.error('Failed to load settings', { error: message }, err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    setDraftConfig(configFromSetting(selectedSetting));
    setDraftOverrides(overridesFromSetting(selectedSetting));
  }, [selectedSetting]);

  useEffect(() => {
    if (!selectedSetting) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setPreviewLoading(true);
      setPreviewError(null);
      try {
        const overridesSupported = canEditTemplate(selectedSetting.notification_type);
        const data = await notificationSettingsService.getPreview(
          selectedSetting.notification_type,
          overridesSupported ? draftOverrides : undefined
        );
        if (!cancelled) {
          setPreview(data);
        }
      } catch (err) {
        if (!cancelled) {
          setPreviewError(err instanceof Error ? err.message : 'Failed to load preview');
        }
      } finally {
        if (!cancelled) {
          setPreviewLoading(false);
        }
      }
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [selectedSetting, draftOverrides]);

  const handleToggle = async (type: string, enabled: boolean) => {
    setTogglingType(type);
    try {
      await notificationSettingsService.toggleEnabled(type, enabled);
      setSettings((prev) => prev.map((setting) => (
        setting.notification_type === type ? { ...setting, is_enabled: enabled } : setting
      )));
    } catch (err) {
      log.error('Failed to toggle notification', { type }, err as Error);
      await loadSettings();
    } finally {
      setTogglingType(null);
    }
  };

  const handleSelectedToggle = (enabled: boolean) => {
    if (!selectedSetting) {
      return;
    }
    void handleToggle(selectedSetting.notification_type, enabled);
  };

  const handleSave = async () => {
    if (!selectedSetting) {
      return;
    }
    setSaving(true);
    try {
      const merged = await saveNotificationDraft({
        service: notificationSettingsService,
        selectedSetting,
        draftConfig,
        draftOverrides,
        configDirty,
        overridesDirty
      });
      setSettings((prev) => prev.map((setting) => (
        setting.notification_type === selectedSetting.notification_type ? merged : setting
      )));
    } catch (err) {
      if (err instanceof NotificationDraftSaveError && err.latestSetting) {
        const latestSetting = err.latestSetting;
        setSettings((prev) => prev.map((setting) => (
          setting.notification_type === selectedSetting.notification_type ? latestSetting : setting
        )));
      }
      setTestSendResult({
        type: selectedSetting.notification_type,
        success: false,
        message: err instanceof Error ? err.message : 'Failed to save notification settings'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTestSend = async () => {
    if (!selectedSetting || !user?.id) {
      return;
    }
    setSendingTestType(selectedSetting.notification_type);
    setTestSendResult(null);
    try {
      const result = await notificationSettingsService.sendTestNotification(selectedSetting.notification_type, user.id);
      setTestSendResult({
        type: selectedSetting.notification_type,
        success: result.success,
        message: result.message
      });
      window.setTimeout(() => setTestSendResult(null), 5000);
    } finally {
      setSendingTestType(null);
    }
  };

  const isAdmin = user?.roles?.includes('admin') || user?.roles?.includes('Admin');

  if (!isAdmin) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <ShieldAlert className="h-4 w-4" />
          <AlertDescription>Admin access is required to manage email notifications.</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex min-h-[420px] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="email-notifications-redesign">
      <header className="en-topbar">
        <div className="en-crumbs">
          <span>Company Settings</span>
          <ChevronRight className="h-3 w-3" />
          <strong>Email Notifications</strong>
        </div>
      </header>
      <div className="en-content">
      <MasterList
        settings={settings}
        selectedType={selectedType}
        query={query}
        onQueryChange={setQuery}
        counts={counts}
        countsError={countsError}
        togglingType={togglingType}
        onSelect={(setting) => setSelectedType(setting.notification_type)}
        onToggle={handleToggle}
      />
      <DetailPane
        setting={selectedSetting}
        draftConfig={draftConfig}
        draftOverrides={draftOverrides}
        availableUsers={availableUsers}
        userRoles={userRoles}
        encompassRoles={encompassRoles}
        encompassRolesError={encompassRolesError}
        countSummary={selectedSetting ? counts[selectedSetting.notification_type] : undefined}
        dirty={dirty}
        saving={saving}
        sendingTest={sendingTestType === selectedSetting?.notification_type}
        preview={preview}
        previewLoading={previewLoading}
        previewError={previewError}
        showPreview={showPreview}
        countsError={countsError}
        onToggleEnabled={handleSelectedToggle}
        onTogglePreview={() => setShowPreview((current) => !current)}
        onConfigChange={setDraftConfig}
        onResetConfig={() => setDraftConfig(null)}
        onUndoConfigReset={() => setDraftConfig(persistedConfig)}
        onOverridesChange={setDraftOverrides}
        onSave={handleSave}
        onTestSend={handleTestSend}
      />
      {testSendResult && (
        <div className="absolute bottom-4 left-1/2 z-20 -translate-x-1/2">
          <Alert variant={testSendResult.success ? 'default' : 'destructive'} className="w-[420px] bg-background shadow-lg">
            {testSendResult.success ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
            <AlertDescription>{testSendResult.message}</AlertDescription>
          </Alert>
        </div>
      )}
      </div>
    </div>
  );
}
